import React, { useEffect, useMemo, useRef, useState } from "react"
import { COPY } from "./config/copy"
import { getCountriesForRegion } from "./config/region-countries"
import { US_COUNTRY_NAME, US_STATES } from "./config/us-states"
import {
  getNextTargetForAnswer,
  getQuestionById,
  getQuestionStepsForPath,
  isTerminalNext,
} from "./data/question-utils"
import { trackAnalytics } from "./analytics"
import { NoMatch } from "./components/NoMatch"
import { ProgressBar } from "./components/ProgressBar"
import { QuestionStep } from "./components/QuestionStep"
import { ResultsScreen } from "./components/ResultsScreen"
import { ActionsRow } from "./components/ActionsRow"
import type { MatchRequestPayload } from "./types/matching"
import type { Product } from "./types/product"
import type { Answers } from "./types/questions"
import type { SubmissionPayload } from "./types/submission"
import type { QuestionGraph } from "./data/types"
import type { WidgetDataSource } from "./data/types"

const WIDGET_VERSION = "0.2.0"

type Stage =
  | "loading"
  | "question"
  | "matching"
  | "results"
  | "no_match"
  | "load_error"

interface AppProps {
  dataSource: WidgetDataSource
}

function uuid(): string {
  return (
    globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)
  )
}

function answersToRecord(answers: Answers): Record<string, string> {
  return Object.fromEntries(
    Object.entries(answers).filter(
      (entry): entry is [string, string] =>
        typeof entry[1] === "string" && entry[1] !== "",
    ),
  )
}

interface LeadFormValues {
  firstName: string
  lastName: string
  company: string
  city: string
  state: string
  country: string
  email: string
}

const EMPTY_LEAD_FORM: LeadFormValues = {
  firstName: "",
  lastName: "",
  company: "",
  city: "",
  state: "",
  country: "",
  email: "",
}

export function App({ dataSource }: AppProps): JSX.Element {
  const [stage, setStage] = useState<Stage>("loading")
  const [questionGraph, setQuestionGraph] = useState<QuestionGraph | null>(null)
  const [rootQuestionId, setRootQuestionId] = useState("")
  const [questionSetVersion, setQuestionSetVersion] = useState("")
  const [answers, setAnswers] = useState<Answers>({})
  const [currentQuestionId, setCurrentQuestionId] = useState("")
  const [questionHistory, setQuestionHistory] = useState<string[]>([])
  const [leadForm, setLeadForm] = useState<LeadFormValues>(EMPTY_LEAD_FORM)
  const [leadFormError, setLeadFormError] = useState<string | null>(null)
  const [leadEmailCaptureDone, setLeadEmailCaptureDone] = useState(false)
  const [leadSubmitting, setLeadSubmitting] = useState(false)
  const leadRequest = useRef({ generation: 0, pending: false })
  const [sessionId, setSessionId] = useState(uuid())
  const [currentCandidates, setCurrentCandidates] = useState<Product[]>([])
  const [lastMatchPool, setLastMatchPool] = useState<Product[]>([])

  useEffect(() => {
    let isActive = true

    dataSource
      .loadQuestions()
      .then((loaded) => {
        if (!isActive) {
          return
        }
        setQuestionGraph(loaded.questionGraph)
        setRootQuestionId(loaded.rootQuestionId)
        setQuestionSetVersion(loaded.version)
        setCurrentQuestionId(loaded.rootQuestionId)
        setQuestionHistory([loaded.rootQuestionId])
        setStage("question")
        trackAnalytics({
          event: "widget_started",
          payload: {
            sessionId,
            widgetVersion: WIDGET_VERSION,
            questionSetVersion: loaded.version,
            pageUri: window.location.href,
          },
        })
      })
      .catch(() => {
        if (!isActive) {
          return
        }
        setStage("load_error")
      })

    return () => {
      isActive = false
    }
  }, [dataSource])

  const graph = questionGraph

  const currentQuestion =
    graph !== null ? getQuestionById(graph, currentQuestionId) : undefined

  const displayedProducts = useMemo(
    () => currentCandidates.slice(0, 3),
    [currentCandidates],
  )
  const countryOptions = useMemo(
    () => getCountriesForRegion(answers.region_origin ?? ""),
    [answers.region_origin],
  )
  const isUnitedStatesCountry = leadForm.country === US_COUNTRY_NAME

  useEffect(() => {
    if (countryOptions.length !== 1 || leadForm.country !== "") {
      return
    }

    setLeadForm((current) => ({
      ...current,
      country: countryOptions[0] ?? "",
      state: "",
    }))
  }, [countryOptions, leadForm.country])

  const totalQuestionSteps =
    graph !== null ? getQuestionStepsForPath(graph, rootQuestionId, answers) : 0
  const totalSteps = totalQuestionSteps
  const currentProgressStep = Math.min(questionHistory.length, totalSteps)
  const isFirstQuestion = questionHistory.length === 1
  const isQuestionNextDisabled = currentQuestion
    ? !answers[currentQuestion.id]
    : true

  const refineLeadForm = !leadEmailCaptureDone ? (
    <div className="csw-refine-panel">
      <h2>Let's refine this together</h2>
      <p>
        Use the fields below to connect with our team and ensure the best fit
        for your exact requirements
      </p>
      <form
        className="csw-refine-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          void handleSubmitResultsEmail()
        }}
      >
        <input
          className="csw-refine-input"
          type="text"
          name="firstName"
          value={leadForm.firstName}
          required
          aria-required="true"
          onChange={(event) => {
            updateLeadFormField("firstName", event.target.value)
          }}
          placeholder={COPY.email.firstNamePlaceholder}
          aria-label="First name"
        />
        <input
          className="csw-refine-input"
          type="text"
          name="lastName"
          value={leadForm.lastName}
          required
          aria-required="true"
          onChange={(event) => {
            updateLeadFormField("lastName", event.target.value)
          }}
          placeholder={COPY.email.lastNamePlaceholder}
          aria-label="Last name"
        />
        <input
          className="csw-refine-input"
          type="text"
          name="company"
          value={leadForm.company}
          required
          aria-required="true"
          onChange={(event) => {
            updateLeadFormField("company", event.target.value)
          }}
          placeholder={COPY.email.companyPlaceholder}
          aria-label="Company name"
        />
        <select
          className={`csw-refine-input ${leadForm.country ? "" : "is-placeholder"}`}
          name="country"
          value={leadForm.country}
          required
          aria-required="true"
          onChange={(event) => {
            updateLeadFormField("country", event.target.value)
          }}
          aria-label="Country"
        >
          <option value="" disabled hidden={leadForm.country !== ""}>
            {COPY.email.countryPlaceholder}
          </option>
          {countryOptions.map((country) => (
            <option key={country} value={country}>
              {country}
            </option>
          ))}
        </select>
        <select
          className={`csw-refine-input ${leadForm.state ? "" : "is-placeholder"}`}
          name="state"
          value={isUnitedStatesCountry ? leadForm.state : ""}
          required={isUnitedStatesCountry}
          aria-required={isUnitedStatesCountry ? "true" : undefined}
          disabled={!isUnitedStatesCountry}
          onChange={(event) => {
            updateLeadFormField("state", event.target.value)
          }}
          aria-label="State"
        >
          <option value="" disabled hidden={leadForm.state !== ""}>
            {COPY.email.statePlaceholder}
          </option>
          {US_STATES.map((state) => (
            <option key={state} value={state}>
              {state}
            </option>
          ))}
        </select>
        <input
          className="csw-refine-input"
          type="text"
          name="city"
          value={leadForm.city}
          required
          aria-required="true"
          onChange={(event) => {
            updateLeadFormField("city", event.target.value)
          }}
          placeholder={COPY.email.cityPlaceholder}
          aria-label="City"
        />
        <input
          className="csw-refine-input"
          type="email"
          name="email"
          value={leadForm.email}
          required
          aria-required="true"
          aria-invalid={leadFormError ? true : undefined}
          onChange={(event) => {
            updateLeadFormField("email", event.target.value)
          }}
          placeholder={COPY.email.placeholder}
          aria-label="Email"
        />
        <button className="csw-refine-submit" type="submit" disabled={leadSubmitting}>
          {leadSubmitting ? COPY.email.sending : COPY.email.cta}
        </button>
      </form>
      {leadFormError ? (
        <p className="csw-refine-error" role="alert">{leadFormError}</p>
      ) : null}
    </div>
  ) : (
    <p className="csw-refine-thanks">{COPY.email.thanks}</p>
  )

  function handleSelect(questionId: string, value: string): void {
    setAnswers((prev) => ({ ...prev, [questionId]: value }))
  }

  function handleNext(): void {
    if (!graph || !currentQuestion) {
      return
    }

    const currentAnswer = answers[currentQuestion.id]
    if (!currentAnswer) return

    trackAnalytics({
      event: "question_answered",
      payload: {
        questionId: currentQuestion.id,
        value: currentAnswer,
        stepIndex: questionHistory.length - 1,
        sessionId,
        widgetVersion: WIDGET_VERSION,
        questionSetVersion,
      },
    })

    const nextTarget = getNextTargetForAnswer(
      graph,
      currentQuestion.id,
      currentAnswer,
    )
    if (!nextTarget) {
      return
    }

    if (isTerminalNext(nextTarget, graph)) {
      setStage("matching")
      const answerRecord = answersToRecord({
        ...answers,
        [currentQuestion.id]: currentAnswer,
      })

      dataSource
        .matchProducts(buildMatchRequestPayload(answerRecord))
        .then((candidates) => {
          setLastMatchPool(candidates)
          setCurrentCandidates(candidates)

          if (candidates.length === 0) {
            setStage("no_match")
            trackAnalytics({
              event: "no_match_reached",
              payload: { answersAtNoMatch: answers, sessionId },
            })
            return
          }

          trackResultsDisplayed(candidates)
          setStage("results")
        })
        .catch(() => {
          setStage("load_error")
        })
      return
    }

    setCurrentQuestionId(nextTarget)
    setQuestionHistory((prev) => [...prev, nextTarget])
  }

  function handleBack(): void {
    if (stage !== "question" || questionHistory.length <= 1) return

    const previousQuestionId = questionHistory[questionHistory.length - 2]
    if (!previousQuestionId || !graph) return

    setAnswers((prev) => {
      const next = { ...prev }
      if (currentQuestion) {
        delete next[currentQuestion.id]
      }
      return next
    })
    setQuestionHistory((prev) => prev.slice(0, -1))
    setCurrentQuestionId(previousQuestionId)
    setCurrentCandidates([])
  }

  function trackResultsDisplayed(candidates: Product[]): void {
    const offered = candidates.slice(0, 3)
    trackAnalytics({
      event: "results_shown",
      payload: {
        sessionId,
        widgetVersion: WIDGET_VERSION,
        questionSetVersion,
        productIds: offered.map((product) => product.id),
        productSlugs: offered.map((product) => product.slug),
      },
    })

    trackAnalytics({
      event: "products_offered",
      payload: {
        sessionId,
        productIds: offered.map((product) => product.id),
        productSlugs: offered.map((product) => product.slug),
        productNames: offered.map((product) => product.name),
      },
    })
  }

  function buildMatchRequestPayload(
    answerRecord: Record<string, string>,
  ): MatchRequestPayload {
    return {
      answers: answerRecord,
      maxResults: 3,
      sessionId,
      pageUri: window.location.href,
      widgetVersion: WIDGET_VERSION,
      questionSetVersion,
    }
  }

  function updateLeadFormField(
    field: keyof LeadFormValues,
    value: string,
  ): void {
    setLeadForm((current) => {
      if (field === "country" && value !== US_COUNTRY_NAME) {
        return { ...current, country: value, state: "" }
      }

      return { ...current, [field]: value }
    })
    setLeadFormError(null)
  }

  function buildLeadSubmissionPayload(
    values: LeadFormValues,
  ): SubmissionPayload {
    return {
      sessionId,
      submittedAt: new Date().toISOString(),
      pageUri: window.location.href,
      widgetVersion: WIDGET_VERSION,
      questionSetVersion,
      answers,
      firstName: values.firstName,
      lastName: values.lastName,
      company: values.company,
      city: values.city,
      state: values.state,
      country: values.country,
      email: values.email,
      offeredProducts: displayedProducts.map((product) => ({
        id: product.id,
        slug: product.slug,
        name: product.name,
        dataSheetUrl: product.cta.dataSheetUrl,
      })),
    }
  }

  async function handleSubmitResultsEmail(): Promise<void> {
    if (leadRequest.current.pending) return
    const values = {
      firstName: leadForm.firstName.trim(),
      lastName: leadForm.lastName.trim(),
      company: leadForm.company.trim(),
      city: leadForm.city.trim(),
      state: isUnitedStatesCountry ? leadForm.state.trim() : "",
      country: leadForm.country.trim(),
      email: leadForm.email.trim(),
    }

    if (values.firstName.length === 0) {
      setLeadFormError(COPY.email.firstNameRequired)
      return
    }

    if (values.lastName.length === 0) {
      setLeadFormError(COPY.email.lastNameRequired)
      return
    }

    if (values.company.length === 0) {
      setLeadFormError(COPY.email.companyRequired)
      return
    }

    if (values.city.length === 0) {
      setLeadFormError(COPY.email.cityRequired)
      return
    }

    if (isUnitedStatesCountry && values.state.length === 0) {
      setLeadFormError(COPY.email.stateRequired)
      return
    }

    if (values.country.length === 0) {
      setLeadFormError(COPY.email.countryRequired)
      return
    }

    if (values.email.length === 0) {
      setLeadFormError(COPY.email.required)
      return
    }

    const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)
    if (!validEmail) {
      setLeadFormError(COPY.email.invalid)
      return
    }

    setLeadFormError(null)
    leadRequest.current.pending = true
    const generation = leadRequest.current.generation
    setLeadSubmitting(true)
    try {
      await dataSource.submitLead(buildLeadSubmissionPayload(values))
      if (leadRequest.current.generation !== generation) return
      trackAnalytics({ event: "email_submitted", payload: { sessionId } })
      setLeadEmailCaptureDone(true)
    } catch {
      if (leadRequest.current.generation === generation) {
        setLeadFormError(COPY.email.failure)
      }
    } finally {
      if (leadRequest.current.generation === generation) {
        leadRequest.current.pending = false
        setLeadSubmitting(false)
      }
    }
  }

  function restart(): void {
    if (!graph || !rootQuestionId) {
      return
    }
    leadRequest.current = { generation: leadRequest.current.generation + 1, pending: false }
    setLeadSubmitting(false)
    setAnswers({})
    setCurrentQuestionId(rootQuestionId)
    setQuestionHistory([rootQuestionId])
    setLeadForm(EMPTY_LEAD_FORM)
    setLeadFormError(null)
    setLeadEmailCaptureDone(false)
    setSessionId(uuid())
    setLastMatchPool([])
    setCurrentCandidates([])
    setStage("question")
  }

  const primaryProduct = displayedProducts[0]

  return (
    // Root widget shell and container-query boundary.
    <div
      className="w-full min-h-[985px] @container"
      data-version={WIDGET_VERSION}
    >
      <div className="relative w-full min-h-[985px] flex flex-col justify-center">
        {/* Background layer: full width on small containers, 45.04% on large. */}
        <div className="absolute top-0 left-0 bottom-0 w-full bg-csw-panel @5xl:w-[45.04%]" />
        {/* Foreground layout: stacked by default, split into two columns at large container sizes. */}
        <div
          className={`flex flex-col relative z-1 w-full  ${stage !== "results" ? " @5xl:pt-40 pt-20 pb-20" : "pt-20"} @5xl:flex-row  `}
        >
          {/* Left column: brand/context panel. */}
          <aside className="csw-top-panel w-full text-csw-white @5xl:w-[45.04%]">
            <div className="csw-top-panel-content px-16 @max-5xl:py-16">
              {stage !== "results" || !primaryProduct ? (
                // Left panel in pre-results states.
                <div className="">
                  <h1 className="text-[60px] leading-[120%]">
                    {COPY.left.title}
                  </h1>
                  <p className="text-2xl mt-4 font-light flex flex-wrap gap-4">
                    <span className="text-csw-white-muted text-nowrap">
                      {COPY.left.helpPrefix}{" "}
                    </span>
                    <a
                      className="text-csw-white border-b border-solid border-csw-white hover:text-csw-white-muted hover:border-csw-white-muted text-nowrap font-extralight"
                      href="#"
                      onClick={(event) => event.preventDefault()}
                    >
                      {COPY.left.helpLink}
                    </a>
                  </p>
                  {stage !== "question" || !isFirstQuestion ? (
                    <div className="mt-20 text-2xl ">
                      <button
                        className=" cursor-pointer"
                        type="button"
                        onClick={restart}
                      >
                        <span className="text-csw-white border-b border-solid border-csw-white hover:text-csw-white-muted hover:border-csw-white-muted font-extralight">
                          {COPY.left.restartLink}
                        </span>
                      </button>
                      <span className="csw-inline-muted">
                        {" "}
                        {COPY.left.restartSuffix}
                      </span>
                    </div>
                  ) : null}
                </div>
              ) : null}
              {stage === "results" && primaryProduct ? (
                // Left panel in final-results state.
                <div className="csw-left-results">
                  <p className="text-csw-white uppercase text-lg font-light">
                    {COPY.results.label}
                  </p>
                  <h1 className="mt-4 text-5xl font-light">
                    {primaryProduct.name}
                  </h1>
                  <img
                    className="csw-left-image mt-8 block mx-auto @max-5xl:w-[min(100%,360px)]"
                    src={primaryProduct.heroImageUrl}
                    alt={primaryProduct.name}
                  />

                  {primaryProduct.cta.teamUrl ? (
                    <a
                      className="csw-left-team-link"
                      href={primaryProduct.cta.teamUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {COPY.results.collaborate}
                    </a>
                  ) : null}

                  <div className="csw-left-ctas">
                    <a
                      className="csw-btn csw-btn-inverse"
                      href={primaryProduct.cta.learnMoreUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {COPY.results.learnMore}
                    </a>
                    {primaryProduct.cta.dataSheetUrl ? (
                      <a
                        className="csw-btn csw-btn-inverse"
                        href={primaryProduct.cta.dataSheetUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          trackAnalytics({
                            event: "datasheet_download_clicked",
                            payload: {
                              productId: primaryProduct.id,
                              productSlug: primaryProduct.slug,
                              dataSheetUrl: primaryProduct.cta.dataSheetUrl,
                              placement: "left_panel",
                              sessionId,
                            },
                          })
                        }}
                      >
                        {COPY.results.dataSheet}
                      </a>
                    ) : null}
                  </div>

                  <button
                    className="csw-left-restart"
                    type="button"
                    onClick={restart}
                  >
                    <span className="text-csw-white no-underline border-b border-solid border-csw-white font-extralight">
                      {COPY.left.restartLink}
                    </span>
                    <span className="csw-inline-muted">
                      {" "}
                      {COPY.left.restartSuffix}
                    </span>
                  </button>
                </div>
              ) : null}
            </div>
          </aside>
          {/* Right column: interactive flow (questions, results). */}
          <main className="csw-bottom-panel w-full text-black flex-1 bg-white flex flex-col justify-start">
            <div className="csw-bottom-panel-content px-16 @max-5xl:py-16 flex flex-1 flex-col">
              {/* Shared transient states. */}
              {stage === "loading" ? (
                <p className="csw-loading">Loading widget...</p>
              ) : null}
              {stage === "load_error" ? (
                <p className="csw-error">Failed to load widget data.</p>
              ) : null}
              {stage === "matching" ? (
                <p className="csw-loading">Finding your products...</p>
              ) : null}

              {/* Main questionnaire state. */}
              {stage === "question" ? (
                <>
                  <ProgressBar
                    currentStep={currentProgressStep}
                    totalSteps={totalSteps}
                  />
                  {currentQuestion ? (
                    <QuestionStep
                      question={currentQuestion}
                      answers={answers}
                      onSelect={handleSelect}
                    />
                  ) : null}
                  {currentQuestion ? (
                    <ActionsRow className="mt-auto pt-8">
                      {!isFirstQuestion ? (
                        <button
                          className="text-lg font-light text-csw-muted cursor-pointer"
                          type="button"
                          onClick={handleBack}
                        >
                          Back
                        </button>
                      ) : (
                        <span />
                      )}

                      <button
                        className="bg-csw-blue text-white text-xl font-light px-10 py-4 cursor-pointer disabled:opacity-50"
                        type="button"
                        disabled={isQuestionNextDisabled}
                        onClick={handleNext}
                      >
                        Next
                      </button>
                    </ActionsRow>
                  ) : null}
                </>
              ) : null}

              {/* No-match fallback state. */}
              {stage === "no_match" ? <NoMatch onRestart={restart} /> : null}

              {/* Final matched results state. */}
              {stage === "results" && primaryProduct ? (
                <>
                  <ResultsScreen product={primaryProduct} />
                  {refineLeadForm}
                </>
              ) : null}
            </div>
          </main>
        </div>
      </div>
    </div>
  )
}
